import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { X, BookOpen, Clock, FileText, Package, ChevronRight, ChevronLeft, Check, Plus, Trash2, Save, ArrowLeft } from 'lucide-react';
import formationService from '../../services/formationService';
import { apiClient } from '../../services/authService';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import './FormationEditPage.css';

const FormationEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration_days: 1,
    is_active: true
  });
  const [availableConsommables, setAvailableConsommables] = useState([]);
  const [selectedConsommables, setSelectedConsommables] = useState([]);
  const [originalConsommables, setOriginalConsommables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formation, setFormation] = useState(null);

  useEffect(() => {
    const fetchFormation = async () => {
      setLoading(true);
      try {
        const response = await formationService.getById(id);
        setFormation(response);
        
        // Pre-fill form data
        setFormData({
          title: response.title || '',
          description: response.description || '',
          duration_days: response.duration_days || 1,
          is_active: response.is_active !== undefined ? response.is_active : true
        });

        // Pre-fill consommables
        const existingConsommables = response.consommables?.map(c => ({
          id: c.id,
          consommable_id: c.id,
          quantity: c.quantity_fixe || 1,
          isExisting: true,
          originalData: c
        })) || [];
        
        setSelectedConsommables(existingConsommables);
        setOriginalConsommables(existingConsommables);
      } catch (err) {
        setError('Erreur lors du chargement de la formation');
        console.error('Error fetching formation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFormation();
  }, [id]);

  useEffect(() => {
    if (step === 2) {
      fetchAvailableConsommables();
    }
  }, [step]);

  const fetchAvailableConsommables = async () => {
    try {
      const response = await apiClient.get('/consommables/');
      setAvailableConsommables(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error fetching consommables:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNext = () => {
    if (!formData.title.trim()) {
      setError('Le titre est obligatoire');
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Update formation
      await formationService.update(id, {
        ...formData,
        duration_days: parseInt(formData.duration_days, 10),
        trainers: formData.trainers
      });

      // Handle consommables - delete removed ones
      const removedConsommables = originalConsommables.filter(
        orig => !selectedConsommables.some(sel => sel.id === orig.id && sel.isExisting)
      );
      
      for (const removed of removedConsommables) {
        try {
          await apiClient.delete(`/consommables/${removed.id}/`);
        } catch (err) {
          console.error('Error deleting consommable:', err);
        }
      }

      // Handle consommables - create new ones
      const newConsommables = selectedConsommables.filter(sel => !sel.isExisting);
      
      console.log('Creating new consommables:', newConsommables);
      console.log('Available consommables:', availableConsommables);
      
      for (const newItem of newConsommables) {
        const consommableId = newItem.consommable_id;
        console.log('Looking for consommable with ID:', consommableId);
        
        const originalConsommable = availableConsommables.find(c => c.id === consommableId);
        console.log('Found original consommable:', originalConsommable);
        
        if (!originalConsommable) {
          console.warn('Consommable not found for ID:', consommableId);
          continue;
        }

        try {
          const response = await apiClient.post('/consommables/', {
            name: originalConsommable.name,
            unit_price: originalConsommable.unit_price,
            type_consommable: originalConsommable.type_consommable,
            quantity_fixe: newItem.quantity,
            quantity_per_student: originalConsommable.quantity_per_student,
            quantity_per_trainer: originalConsommable.quantity_per_trainer,
            quantity_per_visitor: originalConsommable.quantity_per_visitor,
            quantity_per_staff: originalConsommable.quantity_per_staff,
            supplier: originalConsommable.supplier?.id || originalConsommable.supplier,
            formations: [id]
          });
          console.log('Consommable created:', response.data);
        } catch (err) {
          console.error('Error creating consommable:', err);
          console.error('Error response:', err.response?.data);
          throw err;
        }
      }

      // Handle consommables - update existing ones
      const updatedConsommables = selectedConsommables.filter(
        sel => sel.isExisting && sel.quantity !== sel.originalData?.quantity_fixe
      );

      for (const updated of updatedConsommables) {
        try {
          await apiClient.patch(`/consommables/${updated.id}/`, {
            quantity_fixe: updated.quantity
          });
        } catch (err) {
          console.error('Error updating consommable:', err);
        }
      }

      navigate(`/formations/${id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour de la formation');
    } finally {
      setSaving(false);
    }
  };

  const addConsommable = () => {
    setSelectedConsommables(prev => [...prev, {
      id: Date.now(),
      consommable_id: '',
      quantity: 1,
      isExisting: false
    }]);
  };

  const updateConsommable = (index, field, value) => {
    setSelectedConsommables(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeConsommable = (index) => {
    setSelectedConsommables(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>Chargement de la formation...</span>
      </div>
    );
  }

  if (!formation && !loading) {
    return (
      <div className="empty-state">
        <h3>Formation non trouvée</h3>
        <Link to="/formations" className="btn btn-secondary">Retour au catalogue</Link>
      </div>
    );
  }

  return (
    <div className="formation-edit-page">
      <div className="edit-header">
        <Link to={`/formations/${id}`} className="back-link">
          <ArrowLeft size={16} />
          Retour aux détails
        </Link>
        <h1 className="edit-title">
          <BookOpen size={28} />
          Modifier la formation
        </h1>
      </div>

      <div className="edit-container">
        <div className="wizard-steps">
          <div className={`step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <span>Informations</span>
          </div>
          <div className="step-line"></div>
          <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Consommables</span>
          </div>
        </div>

        {error && (
          <div className="edit-error">
            {error}
          </div>
        )}

        {step === 1 && (
          <form className="edit-form" onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
            <div className="form-group">
              <label htmlFor="title">
                <BookOpen size={16} />
                Titre *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Nom de la formation"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">
                <FileText size={16} />
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Description de la formation..."
                rows={4}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="duration_days">
                  <Clock size={16} />
                  Durée (jours) *
                </label>
                <input
                  type="number"
                  id="duration_days"
                  name="duration_days"
                  value={formData.duration_days}
                  onChange={handleChange}
                  min="1"
                  max="365"
                  required
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                  />
                  <span>Formation active</span>
                </label>
              </div>
            </div>

            <div className="edit-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(`/formations/${id}`)}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Suivant
                <ChevronRight size={18} />
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="edit-form">
            <div className="consommables-section">
              <div className="section-header">
                <h3>
                  <Package size={20} />
                  Consommables associés
                </h3>
                <span className="section-hint">Gérez les consommables de cette formation</span>
              </div>

              {selectedConsommables.length === 0 ? (
                <div className="empty-consommables">
                  <Package size={48} />
                  <p>Aucun consommable sélectionné</p>
                  <button
                    type="button"
                    className="btn-add-item"
                    onClick={addConsommable}
                  >
                    <Plus size={16} />
                    Ajouter un consommable
                  </button>
                </div>
              ) : (
                <div className="consommables-list">
                  {selectedConsommables.map((item, index) => (
                    <div key={item.id} className="consommable-row">
                      <select
                        value={item.consommable_id}
                        onChange={(e) => updateConsommable(index, 'consommable_id', e.target.value)}
                        className="consommable-select"
                        disabled={item.isExisting}
                      >
                        <option value="">
                          {item.isExisting 
                            ? item.originalData?.name 
                            : 'Sélectionner un consommable...'
                          }
                        </option>
                        {!item.isExisting && availableConsommables.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name} ({c.unit_price} €)
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateConsommable(index, 'quantity', parseInt(e.target.value) || 1)}
                        min="1"
                        className="quantity-input"
                        placeholder="Qté"
                      />
                      <button
                        type="button"
                        className="btn-remove"
                        onClick={() => removeConsommable(index)}
                        title="Supprimer ce consommable"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn-add-item"
                    onClick={addConsommable}
                  >
                    <Plus size={16} />
                    Ajouter un consommable
                  </button>
                </div>
              )}
            </div>

            <div className="edit-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleBack}
                disabled={saving}
              >
                <ChevronLeft size={18} />
                Retour
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || selectedConsommables.some(c => !c.isExisting && !c.consommable_id)}
              >
                {saving ? 'Enregistrement...' : (
                  <>
                    <Save size={18} />
                    Enregistrer les modifications
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormationEditPage;
